import React, {
    forwardRef,
    ReactElement,
    ReactNode,
    Ref,
    useImperativeHandle,
    useRef,
} from "react";
import RenderFormChild from "./renderFormChild";
import { DeepPartial, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

interface FormProps<T> {
    children: ReactElement | ReactElement[] | ReactNode | ReactNode[];
    onSubmit: (data: T) => void;
    defaultValues?: DeepPartial<T>;
    className?: string;
    shapeValidation?: any;
}

function FormInner<T>(
    {
        children,
        onSubmit,
        defaultValues,
        shapeValidation,
        className,
    }: FormProps<T>,
    ref?: any,
): JSX.Element {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues,
        resolver: yupResolver(shapeValidation),
    });

    const style = {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '20px',
        padding: '20px'
    }


    const formRef = useRef<HTMLFormElement>(null);

    useImperativeHandle(
        ref, () => {
            return {
                submit: () => {
                    formRef?.current?.dispatchEvent(
                        new Event("submit", { bubbles: true, cancelable: true }),
                    );
                },
            };
        });

    
    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit((data) => onSubmit(data as T))}
            className={className} style={style}>
            {React.Children.map(children, (child: ReactNode) => {
                return RenderFormChild<T>({ child, register, errors });
            })}
        </form>
    );
}

const Form = forwardRef(FormInner) as <T>(
    props: FormProps<T> & { ref?: Ref<ReactNode> },
) => ReturnType<typeof FormInner>;

export default Form;
